<template>
  <v-container class="pa-3 layout-container">
    <BaseHeader
      @filter="handleFilter"
      @filterButtonClick="handleFilterButtonClick"
      :activeFiltersAmount="amountOfActiveFilters"
    />
    <div class="position-relative">
      <!-- Teleport filter dashboard for small screens -->
      <Teleport
        to="body"
        :disabled="!isSmallScreen"
      >
        <FilterDashboard
          v-if="showFilters && isSmallScreen"
          v-model:show="showFilters"
          @filter-change="handleFilter"
          :active-filters="activeFilters"
          :topics="topics"
          :asset-types="assetTypesWithCounts"
          :image-sources="imageSourcesWithCounts"
          class="filter-overlay"
        />
      </Teleport>

      <!-- Layout container -->
      <div :class="isSmallScreen ? '' : 'd-flex flex-row ma-0 g-0'">
        <!-- Filter column - only for large screens -->
        <div
          v-if="!isSmallScreen && showFilters"
          class="filter-col"
        >
          <div class="filter-wrapper">
            <!-- Filter dashboard for large screens -->
            <FilterDashboard
              v-model:show="showFilters"
              @filter-change="handleFilter"
              :topics="topics"
              :active-filters="activeFilters"
              :asset-types="assetTypesWithCounts"
              :image-sources="imageSourcesWithCounts"
              class="filter-dashboard"
            />
          </div>
        </div>

        <!-- Content column - for all screen sizes -->
        <div :class="isSmallScreen ? '' : 'content-col'">
          <div
            :class="contentClasses"
            ref="materialContainer"
          >
            <!-- Loading state -->
            <div
              v-if="isLoading && materials.length === 0"
              class="d-flex justify-center align-center empty-state-container"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
            </div>

            <!-- Error state -->
            <v-alert
              v-else-if="queryError"
              type="error"
              class="empty-state-container mx-4"
              :text="
                retryCount >= 3
                  ? $t('ad-materials.messages.service-unavailable')
                  : $t('ad-materials.messages.error-loading')
              "
              variant="tonal"
              border="start"
              density="comfortable"
            >
              <template v-slot:append>
                <v-btn
                  v-if="retryCount < 3"
                  color="error"
                  variant="tonal"
                  class="font-weight-bold"
                  @click="handleRetry"
                >
                  {{ $t('ad-materials.actions.retry') }}
                </v-btn>
              </template>
            </v-alert>

            <!-- Empty state -->
            <v-alert
              v-else-if="!flattenedMaterials.length && !isLoading"
              type="info"
              class="empty-state-container"
              variant="tonal"
              border="start"
              density="comfortable"
            >
              <template v-slot:prepend>
                <div class="d-flex align-center justify-center h-100">
                  <v-icon
                    color="info"
                    size="large"
                    >mdi-information-outline</v-icon
                  >
                </div>
              </template>
              <div class="d-flex align-center">
                {{ $t('ad-materials.messages.no-materials') }}
              </div>
            </v-alert>

            <!-- Materials grid -->
            <div
              v-else
              class="materials-container"
            >
              <v-row>
                <v-col
                  v-for="item in flattenedMaterials"
                  :key="item.id"
                  cols="12"
                  sm="6"
                  md="6"
                  lg="4"
                  xl="3"
                >
                  <MaterialCard
                    :name="item.displayName"
                    :url="item.url"
                    :topicId="item.topicId"
                    :imageId="item.assetType === 'SOCIAL_IMAGE' ? item.id : undefined"
                    :imageSource="item.imageSource"
                    :caption="item.topicName || ''"
                    :thumbnail-url="item.previewUrl"
                    :is-new="isNewMaterial(item.createdAt)"
                    :icon="item.icon"
                    class="h-100"
                    @preview="materialPreviewId = item.id"
                  />
                </v-col>
              </v-row>

              <!-- Loading indicator for pagination -->
              <div
                v-if="isLoading && materials.length > 0"
                class="d-flex justify-center py-4 w-100"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </div>

              <!-- Infinite scroll trigger -->
              <div
                ref="scrollTrigger"
                class="scroll-trigger"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <MaterialPreview
      v-model="materialPreviewId"
      :material="materialPreview"
    />
  </v-container>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useQuery } from '@vue/apollo-composable'
import { computed, onActivated, onMounted, onUnmounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router'

import MaterialCard from '@/modules/landingpages/MaterialCard.vue'

// Remove SVG imports that are causing TypeScript errors
import AD_MATERIALS from './AdMaterials.gql'
import BaseHeader from './BaseHeader.vue'
import FilterDashboard from './FilterDashboard.vue'
import MaterialPreview from './MaterialPreview.vue'

interface Topic {
  id: string
  name: string
  count?: number
}

interface AssetTypeFilter {
  type: string
  count: number
}

interface ImageSourceFilter {
  source: string
  count: number
}

export interface AdMaterial {
  id: string
  url: string
  previewUrl: string
  topicName: string
  displayName: string
  imageSource: string
  topicId: string
  assetType: string
  createdAt: string
}

interface AdMaterialsQueryResult {
  adMaterials: {
    adMaterials: AdMaterial[]
    filters: {
      topics: Topic[]
      assetTypes: AssetTypeFilter[]
      imageSources: ImageSourceFilter[]
    }
    meta: {
      total: number
      totalPages: number
    }
  }
}

interface Filters {
  target: 'all' | 'buyers' | 'sellers'
  topics: string[]
  origins: string[]
  search: string
  sort: string
  assetTypes: []
}

// Add this new interface for flattened materials
interface FlattenedMaterial {
  id: string
  displayName: string
  url: string
  previewUrl?: string
  topicId?: string
  topicName?: string
  imageSource?: string
  icon?: string
  assetType?: string
  createdAt: string
}

// Define file type enum for better type safety
type FileType = 'STATIC' | 'VIDEO' | 'IMAGE' | 'DOCUMENT'

// State
const materials = ref<AdMaterial[]>([])
const topics = ref<Topic[]>([]) // Current topics from the latest query
const assetTypesWithCounts = ref<AssetTypeFilter[]>([])
const imageSourcesWithCounts = ref<ImageSourceFilter[]>([])
const showFilters = ref(true)
const currentPage = ref(1)
const hasMorePages = ref(true)
const totalPages = ref(1)
const isLoading = ref(false)
const retryCount = ref(0)
const queryFailed = ref(false)
const scrollTrigger = ref<HTMLElement | null>(null)
const materialContainer = ref<HTMLElement | null>(null)
const isSmallScreen = ref(window.innerWidth <= 960)
const materialPreviewId = ref<string>('')

// Get router instance
const router = useRouter()

// Add a route changed flag
const routeChanged = ref(false)

// Watch for screen size changes using a more precise approach
watch(
  () => window.innerWidth,
  (width) => {
    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960 && !showFilters.value) {
      showFilters.value = true
    }
  },
  { immediate: true }
)

// A single source of truth for all filters
const activeFilters = ref<Filters>({
  target: 'all',
  topics: [],
  origins: [],
  search: '',
  sort: 'new-to-old',
  assetTypes: [],
})

const amountOfActiveFilters = computed(() => {
  const targetCount = activeFilters.value.target === 'all' ? 0 : 1
  const topicsCount = activeFilters.value.topics.length
  const originsCount = activeFilters.value.origins.length
  const assetsCount = activeFilters.value.assetTypes.length

  return targetCount + topicsCount + originsCount + assetsCount
})

// Map frontend sort values to backend sortOrder values
const getSortOrder = (sort: string): string => {
  switch (sort) {
    case 'new-to-old':
      return 'new'
    case 'old-to-new':
      return 'old'
    default:
      return 'new'
  }
}

const materialPreview = computed<AdMaterial | null>(() => {
  if (!materialPreviewId.value) return null
  const material = materials.value.find((m) => m.id === materialPreviewId.value)
  return material || null
})

// Query variables - computed only once and cached
const queryVariables = computed(() => {
  return {
    input: {
      page: currentPage.value,
      itemsPerPage: 20,
      buyers: activeFilters.value.target === 'buyers',
      sellers: activeFilters.value.target === 'sellers',
      imageSources: activeFilters.value.origins,
      topicIds: activeFilters.value.topics,
      search: activeFilters.value.search || '',
      sortOrder: getSortOrder(activeFilters.value.sort),
      assetTypes: activeFilters.value.assetTypes,
    },
  }
})

// Apollo query with reactive variables
const {
  result,
  loading,
  error: queryError,
  refetch,
} = useQuery<AdMaterialsQueryResult>(AD_MATERIALS, () => queryVariables.value, {
  fetchPolicy: 'network-only',
  nextFetchPolicy: 'cache-first',
  notifyOnNetworkStatusChange: true,
})

// Track loading state from Apollo query
watch(loading, (newValue) => {
  isLoading.value = newValue
})

// Watch for query errors and update queryFailed state
watch(queryError, (error) => {
  queryFailed.value = !!error
})

// Update pagination info when query completes
watch(
  result,
  (data) => {
    if (data && data.adMaterials) {
      if (currentPage.value === 1) {
        // Reset materials for first page
        materials.value = data.adMaterials.adMaterials || []

        // Get topics from filters now
        if (data.adMaterials.filters && data.adMaterials.filters.topics) {
          topics.value = data.adMaterials.filters.topics
        } else {
          topics.value = []
        }

        // Get asset types with counts
        if (data.adMaterials.filters && data.adMaterials.filters.assetTypes) {
          assetTypesWithCounts.value = data.adMaterials.filters.assetTypes
        } else {
          assetTypesWithCounts.value = []
        }

        // Get image sources with counts
        if (data.adMaterials.filters && data.adMaterials.filters.imageSources) {
          imageSourcesWithCounts.value = data.adMaterials.filters.imageSources
        } else {
          imageSourcesWithCounts.value = []
        }
      } else {
        // Append materials for subsequent pages
        materials.value = [...materials.value, ...(data.adMaterials.adMaterials || [])]
      }

      // Update pagination info
      if (data.adMaterials.meta) {
        totalPages.value = data.adMaterials.meta.totalPages || 1
        hasMorePages.value = currentPage.value < totalPages.value
      } else {
        hasMorePages.value = false
      }

      // Reset error state on successful query
      if (queryFailed.value) {
        queryFailed.value = false
        retryCount.value = 0
      }
    }
  },
  { immediate: true }
)

// Add Vue Router navigation hooks to handle route changes
onBeforeRouteUpdate((to, from) => {
  // Check if we're navigating to a different route and back
  if (to.name !== from.name) {
    routeChanged.value = true
  }

  // Reset state and refetch data when the route is updated
  resetStateAndRefetch()
})

onBeforeRouteLeave(() => {
  // Clear materials when leaving the route to ensure we refetch when returning
  materials.value = []
  routeChanged.value = true
})

// Add a watch for route changes
watch(
  () => router.currentRoute.value.fullPath,
  () => {
    // If returning to this component after route change
    if (routeChanged.value) {
      resetStateAndRefetch()
      routeChanged.value = false
    }
  }
)

// Add onActivated hook to handle component reactivation with KeepAlive
onActivated(() => {
  // Always refetch when component is activated
  resetStateAndRefetch()
  routeChanged.value = false
})

// Helper function to reset state and refetch data
const resetStateAndRefetch = () => {
  // Reset pagination
  currentPage.value = 1
  hasMorePages.value = true

  // Clear materials to show loading state
  materials.value = []

  // Ensure we are re-triggering the query with clean state
  // Add a small timeout to ensure the component DOM has updated
  setTimeout(() => {
    try {
      // Force a refetch with network-only policy to bypass cache
      refetch({
        fetchPolicy: 'network-only',
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'AdMaterials',
          action: 'resetStateAndRefetch',
        },
      })
    }
  }, 10)
}

const isNewMaterial = (createdAt: string): boolean => {
  if (!createdAt) return false
  const createdDate = new Date(createdAt)
  const now = new Date()
  const oneWeekAgo = new Date(now.setDate(now.getDate() - 7))
  return createdDate >= oneWeekAgo
}

// Update filters and force re-query when required
const updateFilters = (newFilters: Partial<Filters>): void => {
  // Reset to page 1 whenever filters change
  currentPage.value = 1

  // Reset error state when filters change
  if (queryFailed.value) {
    queryFailed.value = false
    retryCount.value = 0
  }

  // Update all filters at once to prevent cascading reactivity
  activeFilters.value = { ...activeFilters.value, ...newFilters }
}

// Define icons for different material types - use string paths instead of imports
const ICONS: Record<FileType, string> = {
  STATIC: '/img/material-static-preview.svg',
  VIDEO: '/img/material-video-preview.svg',
  IMAGE: '/img/material-static-preview.svg',
  DOCUMENT: '/img/material-static-preview.svg',
}

// Flatten materials and their files into a single array
const flattenedMaterials = computed(() => {
  if (!materials.value.length) return []

  const flattened: FlattenedMaterial[] = []

  materials.value.forEach((material) => {
    // Determine file type for icon selection
    // Use URL and assetTypes info to determine proper icon
    let fileType: FileType = 'STATIC'

    // Try to determine file type from URL
    if (material.url) {
      const lowerUrl = material.url.toLowerCase()

      if (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.mov') || lowerUrl.includes('video')) {
        fileType = 'VIDEO'
      } else if (
        lowerUrl.endsWith('.jpg') ||
        lowerUrl.endsWith('.jpeg') ||
        lowerUrl.endsWith('.png') ||
        lowerUrl.endsWith('.gif') ||
        lowerUrl.endsWith('.webp')
      ) {
        fileType = 'IMAGE'
      } else if (
        lowerUrl.endsWith('.pdf') ||
        lowerUrl.endsWith('.doc') ||
        lowerUrl.endsWith('.docx') ||
        lowerUrl.endsWith('.txt')
      ) {
        fileType = 'DOCUMENT'
      }
    }

    flattened.push({
      ...material,
      // Always provide an icon for fallback, even when thumbnail exists
      icon: ICONS[fileType],
    })
  })

  // No need to sort by "new" status since we don't have createdAt anymore
  return flattened
})

// Event handlers - all go through the single updateFilters function
const handleFilter = (filterData: Partial<Filters>): void => {
  updateFilters(filterData)
}

const handleFilterButtonClick = () => {
  showFilters.value = !showFilters.value
}

// Retry function for query errors
const handleRetry = async () => {
  retryCount.value++
  try {
    await refetch()
    if (!queryError.value) {
      // Reset retry count if successful
      retryCount.value = 0
      queryFailed.value = false
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'AdMaterials',
        action: 'handleRetry',
        retryCount: retryCount.value,
      },
      extra: {
        queryVariables: queryVariables.value,
      },
    })
  }
}

// Computed properties
const contentClasses = computed(() => ({
  'ads-materials-content': true,
  'flex-grow-1': true,
  'full-width': isSmallScreen.value,
  'ml-0': !showFilters.value || isSmallScreen.value,
  'ml-4': showFilters.value && !isSmallScreen.value,
  'transition-width': true,
}))

// Set up window scroll event for infinite scrolling and handle responsive behavior
onMounted(() => {
  // Add window resize event listener for more precise breakpoint control
  const handleResize = () => {
    const width = window.innerWidth
    isSmallScreen.value = width <= 960

    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960) {
      showFilters.value = true
    }
  }

  // Initial check
  handleResize()

  // Add resize listener
  window.addEventListener('resize', handleResize)

  const checkForNewPage = () => {
    if (!scrollTrigger.value || !hasMorePages.value || isLoading.value || queryFailed.value) return

    const scrollTriggerRect = scrollTrigger.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    // Check if scroll trigger is visible in viewport (or about to be visible)
    if (scrollTriggerRect.top < viewportHeight + 300) {
      currentPage.value += 1
    }
  }

  // Add scroll event listener
  window.addEventListener('scroll', checkForNewPage)

  // Initial check in case content doesn't fill the page
  setTimeout(checkForNewPage, 500)

  // Clean up event listeners
  onUnmounted(() => {
    window.removeEventListener('scroll', checkForNewPage)
    window.removeEventListener('resize', handleResize)

    // Clear state when component is unmounted
    materials.value = []
    topics.value = []
    currentPage.value = 1
    hasMorePages.value = true
    queryFailed.value = false
    retryCount.value = 0
  })
})
</script>

<style scoped>
.g-0 {
  --v-gutter-x: 0 !important;
}

.filter-col {
  width: 300px !important;
  min-width: 300px;
  max-width: 300px;
  box-sizing: border-box;
  position: relative;
  z-index: 5;
}

.content-col {
  flex: 1;
  position: relative;
  z-index: 1;
}

.filter-wrapper {
  position: sticky;
  top: 0;
  height: calc(100vh - 64px);
  width: 100%;
  box-sizing: border-box;
}

.filter-dashboard {
  position: sticky;
  top: 0;
  height: calc(100vh - 64px);
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  padding-right: 0;
}

.filter-overlay {
  position: fixed;
  top: 64px;
  left: 0;
  height: calc(100vh - 64px);
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  width: 280px;
  z-index: 100;
}

.full-width {
  width: 100%;
}

@media (max-width: 1279px) {
  .filter-dashboard,
  .filter-wrapper {
    top: 55px;
    height: calc(100vh - 120px);
  }

  .filter-overlay {
    top: 55px;
  }
}

@media (max-width: 959px) {
  .filter-col {
    width: 100% !important;
    min-width: 100%;
    max-width: 100%;
  }

  .filter-wrapper,
  .filter-dashboard {
    position: relative;
    height: auto;
    top: 0;
    width: 100%;
  }

  .ads-materials-content {
    margin-left: 0;
    margin-top: 16px;
  }
}

/* Grid column sizing adjustments */
@media (min-width: 960px) and (max-width: 1263px) {
  .filter-col {
    width: 250px !important;
    min-width: 250px;
    max-width: 250px;
  }

  .filter-wrapper,
  .filter-dashboard {
    width: 250px;
  }
}

.ads-materials-content {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.empty-state-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  margin: 16px 0;
}

:deep(.v-alert__prepend),
:deep(.v-alert__append) {
  align-self: center !important;
}

.transition-width {
  transition: margin-left 0.3s ease;
}

.materials-container {
  position: relative;
}

.scroll-trigger {
  width: 100%;
  height: 10px;
  margin-top: 20px;
}

@media (min-width: 1904px) {
  .layout-container {
    max-width: 1800px;
  }
}
</style>
